import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../api";

import ProductCommissionRules from "../components/ProductCommissionRules";

const SCOPE_LABELS = { product: "Product", taxonomy_category: "Category", collection: "Collection" };
const PRIORITIES = { product: 1, taxonomy_category: 2, collection: 3 };
const EMPTY_RULE = { scope: "taxonomy_category", targetKey: "", rate: "" };

function targetLabel(scope, target) {
  if (scope === "taxonomy_category") return target.name;
  return target.title;
}

function targetValue(scope, target) {
  if (scope === "product") return target.legacyId;
  return target.id;
}

export default function AffiliateDetail() {
  const { id } = useParams();
  const [affiliate, setAffiliate] = useState(null);
  const [rules, setRules] = useState([]);
  const [error, setError] = useState("");
  const [defaultRate, setDefaultRate] = useState("");
  const [savingDefault, setSavingDefault] = useState(false);
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm, setRuleForm] = useState(EMPTY_RULE);
  const [search, setSearch] = useState("");
  const [targets, setTargets] = useState([]);
  const [targetLoading, setTargetLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [affiliateResponse, rulesResponse] = await Promise.all([
        api.get(`/affiliates/${id}`),
        api.get(`/affiliates/${id}/commission-rules`),
      ]);
      setAffiliate(affiliateResponse.data);
      setDefaultRate(affiliateResponse.data.commissionRate);
      setRules(rulesResponse.data.filter((rule) => rule.scope !== "product"));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Affiliate not found");
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!showRuleForm || editingRule) return undefined;
    const timer = setTimeout(async () => {
      setTargetLoading(true);
      try {
        const endpoint = ruleForm.scope === "product"
          ? "/products"
          : ruleForm.scope === "taxonomy_category" ? "/taxonomy-categories" : "/collections";
        const response = await api.get(endpoint, { params: { search } });
        setTargets(response.data);
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        setTargetLoading(false);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [showRuleForm, editingRule, ruleForm.scope, search]);

  const saveDefault = async (event) => {
    event.preventDefault();
    const rate = Number(defaultRate);
    if (!Number.isFinite(rate) || rate < 0 || rate > 100) {
      setError("Default commission must be between 0 and 100.");
      return;
    }
    setSavingDefault(true);
    setError("");
    try {
      const response = await api.put(`/affiliates/${id}`, { commissionRate: rate });
      setAffiliate(response.data);
      setDefaultRate(response.data.commissionRate);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingDefault(false);
    }
  };

  const openAddRule = () => {
    setEditingRule(null);
    setRuleForm(EMPTY_RULE);
    setSearch("");
    setTargets([]);
    setError("");
    setShowRuleForm(true);
  };

  const openEditRule = (rule) => {
    setEditingRule(rule);
    setRuleForm({ scope: rule.scope, targetKey: rule.targetId, rate: rule.rate });
    setError("");
    setShowRuleForm(true);
  };

  const saveRule = async (event) => {
    event.preventDefault();
    const rate = Number(ruleForm.rate);
    if (!Number.isFinite(rate) || rate < 0 || rate > 100) {
      setError("Commission rate must be between 0 and 100.");
      return;
    }
    setError("");
    try {
      if (editingRule) {
        await api.put(`/commission-rules/${editingRule.id}`, { rate });
      } else {
        const selected = targets.find((target, index) => `${index}` === ruleForm.targetKey);
        const targetId = selected && targetValue(ruleForm.scope, selected);
        if (!selected || !targetId) {
          setError(ruleForm.scope === "product"
            ? "Select a product with a Shopify legacy ID."
            : "Select a valid target.");
          return;
        }
        await api.post(`/affiliates/${id}/commission-rules`, {
          scope: ruleForm.scope,
          targetId: String(targetId),
          targetTitle: targetLabel(ruleForm.scope, selected),
          rate,
        });
      }
      setShowRuleForm(false);
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const deleteRule = async (rule) => {
    if (!window.confirm(`Delete the ${SCOPE_LABELS[rule.scope]} rule for "${rule.targetTitle || rule.targetId}"?`)) return;
    try {
      await api.delete(`/commission-rules/${rule.id}`);
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (!affiliate && error) return <div className="empty" data-testid="affiliate-detail-error">{error}</div>;
  if (!affiliate) return <div className="loading">Loading…</div>;

  const deleted = Boolean(affiliate.deletedAt || affiliate.status === "deleted");
  return (
    <div data-testid="affiliate-detail-page">
      <header className="page-head">
        <div><Link to="/affiliates" className="back-link">← Affiliates</Link><h1>{affiliate.name}</h1></div>
        <span className={`badge ${affiliate.status === "active" ? "badge-green" : "badge-gray"}`}>{affiliate.status}</span>
      </header>
      {error && <div className="alert" data-testid="affiliate-detail-error">{error}</div>}

      <div className="detail-grid">
        <div><span className="muted">Referral code</span><div><code>{affiliate.code}</code></div></div>
        <div><span className="muted">Referral URL</span><div className="ref-url" title={affiliate.referralUrl}>{affiliate.referralUrl || "—"}</div></div>
        <div><span className="muted">Status</span><div>{affiliate.status}</div></div>
      </div>

      {deleted && <p className="alert">This affiliate is deleted. Historical accounting remains available; referral links and new commissions are disabled.</p>}
      <h2 className="section-title">Default Commission</h2>
      <div className="detail-grid">
        <form onSubmit={saveDefault} style={{ maxWidth: 360 }}>
          <label>Default commission %</label>
          <input type="number" min="0" max="100" step="0.01" required value={defaultRate}
            onChange={(event) => setDefaultRate(event.target.value)} data-testid="default-commission-rate" />
          <button className="btn btn-primary" style={{ marginTop: 12 }} disabled={savingDefault || deleted} data-testid="save-default-rate">
            {savingDefault ? "Saving…" : "Save default rate"}
          </button>
        </form>
        <p className="muted">Used when no more specific rule matches. Synchronized products have individual rates, including explicit 0% rates.</p>
      </div>

      <ProductCommissionRules affiliateId={id} disabled={deleted} />

      <div className="page-head" style={{ marginTop: 28, marginBottom: 12 }}>
        <div><h2 className="section-title" style={{ margin: 0 }}>Category and collection rules</h2></div>
        <button className="btn btn-primary" disabled={deleted} onClick={openAddRule} data-testid="add-commission-rule">+ Add commission rule</button>
      </div>
      <p className="muted small">Product overrides Category. Category overrides Collection. Collection overrides Default. If several collection rules match, the highest matching collection rate wins.</p>
      {rules.length === 0 ? <div className="empty">No special commission rules.</div> : (
        <div className="table-wrap"><table data-testid="affiliate-rules-table">
          <thead><tr><th>Type</th><th>Target</th><th>Rate %</th><th>Priority</th><th>Actions</th></tr></thead>
          <tbody>{rules.map((rule) => (
            <tr key={rule.id}>
              <td>{SCOPE_LABELS[rule.scope]}</td>
              <td><div className="cell-strong">{rule.targetTitle || "Untitled target"}</div><div className="muted small">{rule.targetId}</div></td>
              <td>{rule.rate}</td><td>{PRIORITIES[rule.scope]}</td>
              <td className="actions"><button className="btn btn-ghost btn-sm" disabled={deleted} onClick={() => openEditRule(rule)}>Edit rate</button><button className="btn btn-ghost btn-sm danger" disabled={deleted} onClick={() => deleteRule(rule)}>Delete</button></td>
            </tr>
          ))}</tbody>
        </table></div>
      )}

      <h2 className="section-title">Portal Access</h2>
      <div className="detail-grid">
        <p className="muted">An affiliate can earn commissions without having a login. Portal access lets them log in and view their own results.</p>
        <div><Link className="btn btn-ghost" to="/promoters">Manage portal access</Link></div>
      </div>

      {showRuleForm && (
        <div className="modal-overlay" onClick={() => setShowRuleForm(false)}>
          <div className="modal" onClick={(event) => event.stopPropagation()} data-testid="commission-rule-form">
            <h2>{editingRule ? "Edit commission rate" : "Add commission rule"}</h2>
            <form onSubmit={saveRule}>
              <label>Rule type</label>
              <select value={ruleForm.scope} disabled={Boolean(editingRule)} onChange={(event) => { setRuleForm({ ...ruleForm, scope: event.target.value, targetKey: "" }); setTargets([]); }}>
                <option value="taxonomy_category">Category</option><option value="collection">Collection</option>
              </select>
              {!editingRule && <>
                <label>Search targets</label>
                <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={`Search ${SCOPE_LABELS[ruleForm.scope].toLowerCase()}s`} />
                <label>Target</label>
                <select required value={ruleForm.targetKey} onChange={(event) => setRuleForm({ ...ruleForm, targetKey: event.target.value })}>
                  <option value="">{targetLoading ? "Loading…" : "— select target —"}</option>
                  {targets.map((target, index) => {
                    const invalidProduct = ruleForm.scope === "product" && !target.legacyId;
                    return <option key={target.id || index} value={`${index}`} disabled={invalidProduct}>
                      {targetLabel(ruleForm.scope, target)}{invalidProduct ? " — unavailable (missing legacy ID)" : ""}
                    </option>;
                  })}
                </select>
              </>}
              {editingRule && <p className="muted">{editingRule.targetTitle || editingRule.targetId}</p>}
              <label>Commission %</label>
              <input type="number" min="0" max="100" step="0.01" required value={ruleForm.rate}
                onChange={(event) => setRuleForm({ ...ruleForm, rate: event.target.value })} />
              <div className="modal-actions"><button type="button" className="btn btn-ghost" onClick={() => setShowRuleForm(false)}>Cancel</button><button type="submit" className="btn btn-primary">Save</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
